import {
  AnnouncementsContext,
  type AnnouncementsContextValue,
} from '@tour-kit/announcements';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

type PublishAnnouncements = (value: AnnouncementsContextValue | null) => void;

const PublishAnnouncementsContext = createContext<PublishAnnouncements | null>(
  null,
);

/**
 * Hands the guidance `AnnouncementsProvider` context to the app tree without
 * nesting the app inside it. `children` (the shell) and `guidance` (the
 * TourKit providers) are siblings, so guidance mounting late or remounting
 * never remounts the shell. Updates re-render only `AnnouncementsContext`
 * consumers (e.g. What's New): both element props keep their identity.
 */
export function GuidanceAnnouncementsBridge({
  children,
  guidance,
}: {
  children: ReactNode;
  guidance: ReactNode;
}) {
  const [announcements, setAnnouncements] =
    useState<AnnouncementsContextValue | null>(null);

  return (
    <>
      <AnnouncementsContext.Provider value={announcements}>
        {children}
      </AnnouncementsContext.Provider>
      <PublishAnnouncementsContext.Provider value={setAnnouncements}>
        {guidance}
      </PublishAnnouncementsContext.Provider>
    </>
  );
}

/** Render inside `AnnouncementsProvider` to publish its context to the bridge. */
export function PublishGuidanceAnnouncements() {
  const announcements = useContext(AnnouncementsContext);
  const publish = useContext(PublishAnnouncementsContext);

  useEffect(() => {
    publish?.(announcements);
  }, [announcements, publish]);

  useEffect(() => () => publish?.(null), [publish]);

  return null;
}
